import { ref, watch, readonly } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import type { SymbolTabItem } from '@/views/(public)/trading/types'
import { useAccountStore } from '@/store/account'
import { useSymbolInfoStore } from '@/store/symbolInfo'
import { useSocketStore } from '@/store/socket'

const useTradeTabsStore = defineStore('tradeTabs', () => {
  const accountStore = useAccountStore()
  const symbolInfoStore = useSymbolInfoStore()
  const socketStore = useSocketStore()

  const { selectedAccount, selectedLoginid } = storeToRefs(accountStore)

  const _tabs = ref<SymbolTabItem[]>([])
  const _selectedTab = ref<SymbolTabItem | null>(null)

  // 对外只读，避免外部拿到可写引用
  const tabs = readonly(_tabs)
  const selectedTab = readonly(_selectedTab)

  let lastFetchToken = 0
  // 节流与“等待账户就绪一次”的控制
  let fetchSpecsTimeout: ReturnType<typeof setTimeout> | null = null
  let waitAccountStop: (() => void) | null = null

  // 去重写入 + 防空覆盖（已有数据时拒绝被空数组覆盖）
  const sameBySymbols = (a: SymbolTabItem[], b: SymbolTabItem[]) =>
    a.length === b.length && a.every((t, i) => t.symbol === b[i]?.symbol)

  // 序号用于打断并发写（如 reset 期间的旧写入）
  let writeSeq = 0

  // 仅设置列表，不产生选择或网络副作用
  const setTabs = (newTabs: SymbolTabItem[], updateLocalStorage = true) => {
    // 相同内容则忽略写入，减少无效刷新
    if (sameBySymbols(_tabs.value, newTabs)) return
    // 已有数据时阻止空数组覆盖（防闪烁）
    if (_tabs.value.length > 0 && newTabs.length === 0) return

    // 同引用时克隆为新引用，确保变更触发
    _tabs.value = [...newTabs]

    if (updateLocalStorage) {
      persistTabsForLogin()
    }

    // 若当前选中不在新列表中，则清空选中与订阅意图
    if (_selectedTab.value && !_tabs.value.some((t) => t.symbol === _selectedTab.value!.symbol)) {
      _selectedTab.value = null
      socketStore.setPinned(undefined)
    }
  }

  // 统一切换选中；支持 symbol 或对象；含节流与“等待账户就绪一次”
  const selectTab = (tabOrSymbol: SymbolTabItem | string | null, updateLocalStorage = true) => {
    // 支持传入 symbol 或完整对象，统一解析为列表中的对象
    const tab =
      typeof tabOrSymbol === 'string'
        ? (_tabs.value.find((t) => t.symbol === tabOrSymbol) ?? null)
        : tabOrSymbol

    // 显式清空选中
    if (!tab) {
      if (_selectedTab.value) {
        _selectedTab.value = null
        socketStore.setPinned(undefined)
      }
      return
    }

    // 同一项不重复触发
    const prevActive = _selectedTab.value?.symbol
    if (prevActive === tab.symbol) return

    _selectedTab.value = tab
    socketStore.setPinned(tab.symbol)

    if (updateLocalStorage) {
      persistTabsForLogin()
    }

    // 节流 + 并发保护（token）
    const token = ++lastFetchToken
    if (fetchSpecsTimeout) clearTimeout(fetchSpecsTimeout)

    // 拉取规格；账户未就绪则挂一次性监听
    const doFetch = async () => {
      if (token !== lastFetchToken) return

      if (!selectedAccount.value) {
        if (!waitAccountStop) {
          const stop = watch(
            () => selectedAccount.value,
            async (acc) => {
              if (!acc) return
              try {
                await symbolInfoStore.fetchSpecs([tab], acc)
              } catch (e) {
                console.warn('[trade.tabs] deferred fetchSpecs failed:', e)
              } finally {
                stop()
                waitAccountStop = null
              }
            },
            { immediate: false },
          )
          waitAccountStop = stop
        }
        return
      }

      try {
        await symbolInfoStore.fetchSpecs([tab], selectedAccount.value)
      } catch (e) {
        console.warn('[trade.tabs] fetchSpecs failed:', e)
      }
    }

    // 轻微延迟合并快速切换
    fetchSpecsTimeout = setTimeout(doFetch, 100)
  }

  const keyOf = (loginid: string | number) => `trade:${loginid}:tabs`

  const persistTabsForLogin = (loginid?: string | number) => {
    try {
      const id = loginid ?? selectedLoginid.value
      if (!id) return
      const payload = { tabs: _tabs.value, selected: _selectedTab.value }
      localStorage.setItem(keyOf(id), JSON.stringify(payload))
    } catch (e) {
      console.error('[trade.tabs] persistTabsForLogin error', e)
    }
  }

  const restoreTabsForLogin = (loginid?: string | number): boolean => {
    try {
      const id = loginid ?? selectedLoginid.value
      if (!id) return false
      const raw = localStorage.getItem(keyOf(id))
      if (!raw) return false
      const { tabs, selected } = JSON.parse(raw) as {
        tabs: SymbolTabItem[]
        selected: SymbolTabItem | null
      }
      if (Array.isArray(tabs) && tabs.length > 0) {
        // 先设列表（无副作用），再显式选择（触发副作用）
        setTabs(tabs, false)
        selectTab(selected ?? tabs[0], false)
        return true
      }
    } catch (e) {
      console.error('[trade.tabs] restoreTabsForLogin error', e)
    }
    return false
  }

  // 清理节流/一次性监听，避免脏副作用
  const reset = () => {
    // 打断并发中的 setTabs/selectTab 副作用
    writeSeq++
    _tabs.value = []
    _selectedTab.value = null
    socketStore.setPinned(undefined)
    if (fetchSpecsTimeout) {
      clearTimeout(fetchSpecsTimeout)
      fetchSpecsTimeout = null
    }
    if (waitAccountStop) {
      try {
        waitAccountStop()
      } catch {}
      waitAccountStop = null
    }
    lastFetchToken = 0
  }

  return {
    tabs,
    selectedTab,

    setTabs,
    selectTab,
    persistTabsForLogin,
    restoreTabsForLogin,
    reset,
  }
})

export default useTradeTabsStore
export { useTradeTabsStore }
